import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getProveedores, createProveedor, deleteProveedor, updateProveedor, isAuthenticated } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search, Loader2, Filter, X, Building2, Plus, Trash2, Archive, ArchiveRestore, LogOut, FileText, ExternalLink, MoreVertical } from "lucide-react";

export default function ProveedoresPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [proveedores, setProveedores] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [tabArchivados, setTabArchivados] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [nif, setNif] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [direccion, setDireccion] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addUrl, setAddUrl] = useState("");
  const [addNombre, setAddNombre] = useState("");
  const [addPdfFile, setAddPdfFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const loadProveedores = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = { archivado: tabArchivados || undefined };
      if (busqueda.trim()) filters.busqueda = busqueda.trim();
      if (razonSocial.trim()) filters.razon_social = razonSocial.trim();
      if (nif.trim()) filters.nif = nif.trim();
      if (ciudad.trim()) filters.ciudad = ciudad.trim();
      if (direccion.trim()) filters.direccion = direccion.trim();
      const data = await getProveedores(filters);
      setProveedores(data.proveedores || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar proveedores");
      setProveedores([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [tabArchivados, busqueda, razonSocial, nif, ciudad, direccion]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { replace: true });
      return;
    }
    if (user?.permissions?.proveedores === false) {
      if (user?.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user?.permissions?.formularios !== false) navigate("/formularios", { replace: true });
      else if (user?.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user?.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user?.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user?.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user?.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
      return;
    }
    loadProveedores();
  }, [loadProveedores, navigate, user?.permissions?.proveedores, user?.permissions?.candidatos, user?.permissions?.formularios]);

  const hasActiveFilters = busqueda || razonSocial || nif || ciudad || direccion;
  const clearFilters = () => {
    setBusqueda("");
    setRazonSocial("");
    setNif("");
    setCiudad("");
    setDireccion("");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleAddProveedor = async (e) => {
    e.preventDefault();
    if (!addUrl.trim() && !addPdfFile) {
      toast.error("Indica una URL y/o sube un PDF");
      return;
    }
    setIsCreating(true);
    try {
      const formData = new FormData();
      if (addUrl.trim()) formData.append("url", addUrl.trim());
      if (addNombre.trim()) formData.append("nombre", addNombre.trim());
      if (addPdfFile) formData.append("pdf_file", addPdfFile);
      const res = await createProveedor(formData);
      toast.success("Proveedor creado");
      setAddModalOpen(false);
      setAddUrl("");
      setAddNombre("");
      setAddPdfFile(null);
      if (res?.id) navigate(`/proveedores/${res.id}`);
      else loadProveedores();
    } catch (err) {
      const msg = err.response?.data?.detail ?? "Error al crear proveedor";
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleArchivado = async (proveedor) => {
    try {
      await updateProveedor(proveedor.id, { archivado: !proveedor.archivado });
      toast.success(proveedor.archivado ? "Proveedor restaurado" : "Proveedor archivado");
      loadProveedores();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al actualizar");
    }
  };

  const handleDelete = async (proveedor) => {
    if (!confirm(`¿Eliminar a ${proveedor.nombre || proveedor.razon_social || "este proveedor"}?`)) return;
    try {
      await deleteProveedor(proveedor.id);
      toast.success("Proveedor eliminado");
      loadProveedores();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al eliminar");
    }
  };

  return (
    <div className="min-h-dvh min-w-0">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4 flex-wrap md:flex-nowrap">
          <div className="flex items-center gap-3 shrink-0">
            <button type="button" onClick={() => navigate("/candidatos")} className="flex items-center gap-3 shrink-0">
              <img src="/logo.png" alt="" className="h-8 w-auto" />
            </button>
            <h1 className="text-lg font-semibold tracking-tight">Proveedores</h1>
            <Badge variant="secondary" className="text-xs font-normal min-w-9 tabular-nums justify-center">
              {total}
            </Badge>
          </div>

          <nav className="flex items-center gap-1 shrink-0" aria-label="Navegación principal">
            {user?.permissions?.candidatos !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/candidatos")} className="text-sm">
                Candidatos
              </Button>
            )}
            {user?.permissions?.formularios !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/formularios")} className="text-sm">
                Formularios
              </Button>
            )}
            {user?.permissions?.proveedores !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/proveedores")} className="text-sm bg-muted">
                Proveedores
              </Button>
            )}
            {user?.permissions?.archivos !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/brutalfiles")} className="text-sm">
                Archivos
              </Button>
            )}
            {user?.permissions?.admin !== false && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-sm">
                Admin
              </Button>
            )}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2 text-sm text-muted-foreground px-2 md:px-3">
                    <UserAvatar user={user} size={28} />
                    <span className="hidden md:inline max-w-[120px] truncate">{user.nombre || user.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" style={{ zIndex: 9999 }}>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-sm text-muted-foreground px-2 md:px-3">
                <LogOut className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden md:inline">Salir</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {showFilters && (
        <div className="border-b border-input bg-white animate-fade-in">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-muted-foreground mb-1.5 block">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    placeholder="Nombre, razón social..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </div>
              <div className="w-48">
                <label className="text-xs text-muted-foreground mb-1.5 block">Razón social</label>
                <Input value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} className="h-9" placeholder="Filtrar" />
              </div>
              <div className="w-36">
                <label className="text-xs text-muted-foreground mb-1.5 block">NIF</label>
                <Input value={nif} onChange={(e) => setNif(e.target.value)} className="h-9" placeholder="Filtrar" />
              </div>
              <div className="w-40">
                <label className="text-xs text-muted-foreground mb-1.5 block">Ciudad</label>
                <Input value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="h-9" placeholder="Filtrar" />
              </div>
              <div className="w-48">
                <label className="text-xs text-muted-foreground mb-1.5 block">Dirección</label>
                <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} className="h-9" placeholder="Filtrar" />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="gap-1.5 h-9 font-medium">
                  <X className="w-4 h-4" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-transparent bg-muted/20 pt-4 pb-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 rounded-md border border-input bg-background p-0.5">
              <button
                type="button"
                onClick={() => setTabArchivados(false)}
                className={`h-full px-3 text-sm font-medium rounded-[5px] transition-colors ${!tabArchivados ? "bg-background text-foreground shadow-sm" : "text-muted-foreground/50 hover:text-muted-foreground"}`}
              >
                Activos
              </button>
              <button
                type="button"
                onClick={() => setTabArchivados(true)}
                className={`h-full px-3 text-sm font-medium rounded-[5px] transition-colors ${tabArchivados ? "bg-background text-foreground shadow-sm" : "text-muted-foreground/50 hover:text-muted-foreground"}`}
              >
                Archivados
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              className={`flex h-9 w-9 items-center justify-center rounded-md border border-input transition-colors ${showFilters ? "bg-muted text-foreground" : "bg-background text-muted-foreground hover:text-foreground hover:bg-muted"}`}
              title="Filtros"
              aria-label="Filtros"
            >
              <Filter className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setAddModalOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Añadir proveedor"
              aria-label="Añadir proveedor"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : proveedores.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">
              {hasActiveFilters ? "No hay proveedores con los filtros aplicados" : tabArchivados ? "No hay proveedores archivados" : "Aún no hay proveedores"}
            </p>
            {!tabArchivados && (
              <Button className="mt-4 gap-2" onClick={() => setAddModalOpen(true)}>
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Añadir proveedor
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {proveedores.map((proveedor) => (
              <div
                key={proveedor.id}
                className="rounded-lg border bg-card p-4 hover:bg-muted/30 transition-colors cursor-pointer animate-card-in"
                onClick={() => navigate(`/proveedores/${proveedor.id}`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{proveedor.nombre || proveedor.razon_social || "Sin nombre"}</p>
                    {proveedor.razon_social && proveedor.razon_social !== (proveedor.nombre || "") && (
                      <p className="text-xs text-muted-foreground truncate">{proveedor.razon_social}</p>
                    )}
                    {proveedor.resumen_ia && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{proveedor.resumen_ia}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                      {proveedor.ciudad && <span>{proveedor.ciudad}</span>}
                      {proveedor.email && <span className="truncate">{proveedor.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {proveedor.url && (
                      <a
                        href={proveedor.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                        title="Ver web"
                      >
                        <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </a>
                    )}
                    {proveedor.tiene_pdf && (
                      <span className="p-1.5 rounded text-muted-foreground" title="Tiene PDF">
                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </span>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => handleToggleArchivado(proveedor)}>
                          {proveedor.archivado ? (
                            <><ArchiveRestore className="w-4 h-4 mr-2" /> Restaurar</>
                          ) : (
                            <><Archive className="w-4 h-4 mr-2" /> Archivar</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDelete(proveedor)}>
                          <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Añadir proveedor</DialogTitle>
            <DialogDescription>
              Indica la URL del sitio web y/o sube un PDF. Se extraerá la información (razón social, NIF, contacto, horario, tiempos de envío, etc.).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddProveedor} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">URL del sitio web (opcional)</label>
              <Input
                type="url"
                placeholder="https://..."
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre (opcional)</label>
              <Input
                placeholder="Nombre o razón social"
                value={addNombre}
                onChange={(e) => setAddNombre(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">PDF (opcional)</label>
              <Input
                type="file"
                accept=".pdf,application/pdf"
                className="h-9"
                onChange={(e) => setAddPdfFile(e.target.files?.[0] || null)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={(!addUrl.trim() && !addPdfFile) || isCreating}>
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.5} /> : null}
                Añadir
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
